import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Funnel, Search, X } from 'lucide-react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { useDocumentStore } from '@/features/document'
import { DocumentStatus, ResponseDocumentListItem } from '@/schema'
import { cn } from '@/utils'

import { DOCUMENT_STATUS_DISPLAY_TEXT } from '../../constants'

/** Files created within this window (ms) are considered "still indexing" */
const INDEXING_WINDOW_MS = 5 * 60 * 1000

/**
 * Counts files whose created_at is within the last 5 minutes (still indexing).
 * Uses a stable "now" per render to avoid flicker.
 */
function countFilesStillIndexing(files: ResponseDocumentListItem[]): number {
  if (!files?.length) return 0
  const now = Date.now()
  const cutoff = now - INDEXING_WINDOW_MS
  return files.filter((file) => {
    const createdAt = file?.created_at
    if (!createdAt) return false
    const ts = new Date(createdAt).getTime()
    if (Number.isNaN(ts)) return false
    return ts > cutoff
  }).length
}

interface TFLSidebarSearchAndFilterProps {
  searchText: string
  files: ResponseDocumentListItem[]
  onSearchChange: (value: string) => void
  onSelectStatus: (status: DocumentStatus) => void
  onDeselectStatus: (status: DocumentStatus) => void
}

export const TFLSidebarSearchAndFilter = ({
  searchText,
  files,
  onSearchChange,
  onSelectStatus,
  onDeselectStatus,
}: TFLSidebarSearchAndFilterProps) => {
  const { t } = useTranslation()
  const { filters, setToggleNextFocus, showBookmarked, setShowBookmarked } =
    useDocumentStore()

  const indexingCount = React.useMemo(
    () => countFilesStillIndexing(files ?? []),
    [files]
  )

  return (
    <>
      <div className="flex gap-2 pl-4 pr-2 py-3 items-center justify-center">
        <Input
          leadingIcon={<Search className="size-4 text-gray-500" />}
          placeholder={t('tflViewer.sidebar.search')}
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setToggleNextFocus()
            }
          }}
          className="flex-1 h-9 py-1.5"
          trailingIcon={
            searchText ? <X className="size-3.5 cursor-pointer" /> : undefined
          }
          onTrailingIconClick={
            searchText ? () => onSearchChange('') : undefined
          }
        />
        <Popover>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  variant="text"
                  className="flex-shrink-0 size-8 p-0 flex items-center justify-center relative group"
                  title={t('tflViewer.sidebar.filterStatuses')}
                >
                  <Funnel className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
                  {(!!filters.statuses?.length || showBookmarked) && (
                    <div className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-foreground-fg-error-primary" />
                  )}
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <span>{t('tflViewer.sidebar.filterStatuses')}</span>
            </TooltipContent>
          </Tooltip>
          <PopoverContent
            className="sm:max-w-[160px] p-0"
            side="bottom"
            align="end"
          >
            <div className="py-1 px-1.5 space-y-0.5">
              {Object.keys(DOCUMENT_STATUS_DISPLAY_TEXT).map((key) => {
                const isSelected = filters.statuses.includes(
                  key as DocumentStatus
                )
                const status = key as DocumentStatus
                return (
                  <button
                    key={key}
                    className={cn(
                      'p-sm font-medium text-sm cursor-pointer rounded-sm bg-transparent border-0 w-full hover:bg-primary-hover flex gap-2 items-center justify-between',
                      isSelected ? 'bg-primary-hover' : ''
                    )}
                    onClick={() => {
                      if (isSelected) {
                        onDeselectStatus(status)
                      } else {
                        onSelectStatus(status)
                      }
                    }}
                  >
                    <span>
                      {DOCUMENT_STATUS_DISPLAY_TEXT[key as DocumentStatus]}
                    </span>
                    {isSelected && (
                      <Check
                        size={16}
                        className="text-foreground-fg-brand-primary-600"
                      />
                    )}
                  </button>
                )
              })}
              <Separator className="my-1" />
              <button
                type="button"
                className={cn(
                  'p-sm font-medium text-sm cursor-pointer rounded-sm bg-transparent border-0 w-full hover:bg-primary-hover flex gap-2 items-center justify-between',
                  showBookmarked ? 'bg-primary-hover' : ''
                )}
                onClick={() => setShowBookmarked(!showBookmarked)}
                aria-pressed={showBookmarked}
              >
                <span>{t('tflViewer.sidebar.bookmarkedFiles')}</span>
                {showBookmarked && (
                  <Check
                    size={16}
                    className="text-foreground-fg-brand-primary-600"
                  />
                )}
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {searchText && indexingCount > 0 && (
        <div className="mb-2 pl-4 pr-2">
          <span className="text-xs text-muted-foreground">
            {indexingCount} file{indexingCount === 1 ? ' is' : 's are'} still
            indexing and may not appear in results yet
          </span>
        </div>
      )}
    </>
  )
}
